import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { styled, useTheme } from '@mui/material/styles'
import { forwardRef, ForwardRefRenderFunction, memo, useEffect, useRef, useState } from 'react'
import { useConstant } from '../../../../hooks/useConstant'
import { RouteUtils } from '../../../../utils/route-utils'
import { useGetBoundingClientRect } from '../../../../hooks/useGetBoundingClientRect'
import useMediaQuery from '@mui/material/useMediaQuery'
import { GraphTooltip } from '../GraphTooltip'
import { Layer } from '../../../../../oasis-indexer/api'

interface GraphBaseProps {
  disabled?: boolean
  transparent?: boolean
}

interface GraphProps extends GraphBaseProps {
  scale: number
  // TODO: Consider moving this to a state management solution
  selectedLayer?: Layer
  setSelectedLayer: (value: Layer) => void
  setActiveMobileGraphTooltip: (layer: Layer | null) => void
}

interface GraphSvgProps extends GraphBaseProps {
  Layer?: Layer
}

const GraphStyled = styled('svg', {
  shouldForwardProp: prop =>
    !(['disabled', 'transparent', 'Layer'] as (keyof GraphSvgProps)[]).includes(prop as keyof GraphSvgProps),
})<GraphSvgProps>(({ disabled, transparent, Layer: layer }) => ({
  position: 'absolute',
  left: '50%',
  top: '50%',
  width: '80%',
  height: '80%',
  transform: 'translate(-50%, -50%)',
  ...(disabled || transparent
    ? {
        pointerEvents: 'none',
      }
    : {}),
  ...(transparent
    ? {
        opacity: 0.25,
      }
    : {
        'g[id], circle[id]': {
          opacity: 0.9,
        },
      }),
  [`g[id^=${layer}], circle[id^=${layer}]`]: {
    opacity: '1',
  },
  [`ellipse[id=${Layer.consensus}-outer-circle]`]: {
    opacity: '0.25',
    '&:hover': {
      opacity: '0.25',
    },
  },
  '[id]:not([aria-disabled="true"])': {
    cursor: 'pointer',
    '&:hover': {
      opacity: '1',
    },
  },
}))

const GraphCmp: ForwardRefRenderFunction<SVGSVGElement, GraphProps> = (
  {
    disabled = false,
    transparent = false,
    selectedLayer,
    setSelectedLayer,
    scale,
    setActiveMobileGraphTooltip,
  },
  ref,
) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const enabledLayers = useConstant(() => RouteUtils.getEnabledLayers())
  const consensusRef = useRef<SVGCircleElement>(null)
  const consensusRefInnerCircle = useRef<SVGCircleElement>(null)
  const emeraldRef = useRef<SVGGElement>(null)
  const sapphireRef = useRef<SVGGElement>(null)
  const cipherRef = useRef<SVGGElement>(null)

  const [showZoomedInLayerTooltip, setShowZoomedInLayerTooltip] = useState<Layer | null>(null)

  const [emeraldRect, emeraldRectRefresh] = useGetBoundingClientRect(emeraldRef)
  const [sapphireRect, sapphireRectRefresh] = useGetBoundingClientRect(sapphireRef)
  const [cipherRect, cipherRectRefresh] = useGetBoundingClientRect(cipherRef)
  const [consensusRect, consensusRectRefresh] = useGetBoundingClientRect(consensusRef)
  const [consensusInnerCircleRect, consensusInnerCircleRectRefresh] =
    useGetBoundingClientRect(consensusRefInnerCircle)

  useEffect(() => {
    emeraldRectRefresh()
    sapphireRectRefresh()
    cipherRectRefresh()
    consensusRectRefresh()
    consensusInnerCircleRectRefresh()
  }, [
    scale,
    emeraldRectRefresh,
    sapphireRectRefresh,
    cipherRectRefresh,
    consensusRectRefresh,
    consensusInnerCircleRectRefresh,
  ])

  useEffect(() => {
    setShowZoomedInLayerTooltip(null)
    setActiveMobileGraphTooltip(null)
    // should only close tooltips on isMobile change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile])

  const isLayerDisabled = (Layer: Layer) => {
    return !enabledLayers.includes(Layer)
  }

  const disabledMap = useConstant<{
    [key in Layer]: boolean
  }>(() => ({
    [Layer.emerald]: isLayerDisabled(Layer.emerald),
    [Layer.consensus]: isLayerDisabled(Layer.consensus),
    [Layer.cipher]: isLayerDisabled(Layer.cipher),
    [Layer.sapphire]: isLayerDisabled(Layer.sapphire),
  }))

  const onSelectLayer = (Layer: Layer) => {
    if (isMobile && (disabledMap[Layer] || (!disabledMap[Layer] && selectedLayer === Layer))) {
      setActiveMobileGraphTooltip(Layer)

      return
    }

    if (selectedLayer === Layer && RouteUtils.getEnabledLayers().includes(selectedLayer)) {
      navigate(RouteUtils.getDashboardRoute(selectedLayer))

      return
    }

    setSelectedLayer(Layer)
  }

  const handleOpenZoomedInLayerTooltip = (layer: Layer) => {
    if (selectedLayer !== layer) {
      return
    }

    setShowZoomedInLayerTooltip(layer)
  }

  const handleCloseZoomedInLayerTooltip = () => {
    setShowZoomedInLayerTooltip(null)
  }

  return (
    <GraphStyled
      width="100%"
      height="100%"
      viewBox="0 0 396 327"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      disabled={disabled}
      transparent={transparent}
      Layer={selectedLayer}
      preserveAspectRatio="xMidYMid slice"
      ref={ref}
    >
      <path
        d="M195.671 64.2157L155.945 38.4363"
        stroke="url(#paint0_linear_3537_177608)"
        strokeWidth="0.845226"
      />
      <path
        d="M195.672 64.2158L235.397 35.6895"
        stroke="url(#paint1_linear_3537_177608)"
        strokeWidth="0.845226"
      />
      <path d="M188.906 173.672L305.547 128.242" stroke="#00FEFF" strokeWidth="1.26784" />
      <path d="M187.219 174.729L195.882 65.2723" stroke="#00FEFF" strokeWidth="1.26784" />
      <path d="M188.208 172.005L131.57 247.28" stroke="#00FEFF" strokeWidth="1.26784" />
      <path d="M112.748 263.626L74 290.853" stroke="url(#paint2_linear_3537_177608)" strokeWidth="0.845226" />
      <path
        d="M125.999 264.07L158.635 300.495"
        stroke="url(#paint3_linear_3537_177608)"
        strokeWidth="0.845226"
      />
      <ellipse
        id={`${Layer.consensus}-outer-circle`}
        onClick={() => onSelectLayer(Layer.consensus)}
        aria-disabled={disabledMap[Layer.consensus]}
        cx="188.875"
        cy="171.547"
        rx="52.9926"
        ry="52.9926"
        transform="rotate(-90 188.875 171.547)"
        fill="#00FEFF"
      />
      <GraphTooltip
        offsetWidth={consensusInnerCircleRect?.width}
        offsetHeight={consensusInnerCircleRect?.height}
        layer={Layer.consensus}
      >
        <circle
          ref={consensusRefInnerCircle}
          aria-disabled={disabledMap[Layer.consensus]}
          id={`${Layer.consensus}-inner-circle`}
          onClick={() => onSelectLayer(Layer.consensus)}
          cx="188.875"
          cy="171.546"
          r="37.3907"
          transform="rotate(-90 188.875 171.546)"
          fill="#00FEFF"
        />
      </GraphTooltip>

      <GraphTooltip
        offsetWidth={consensusRect?.width}
        offsetHeight={consensusRect?.height}
        layer={Layer.consensus}
      >
        <circle
          ref={consensusRef}
          id={`${Layer.consensus}-circle`}
          aria-disabled={disabledMap[Layer.consensus]}
          onClick={() => onSelectLayer(Layer.consensus)}
          cx="189.074"
          cy="171.547"
          r="25.5236"
          transform="rotate(-90 189.074 171.547)"
          fill="#191932"
        />
      </GraphTooltip>
      <path
        id={`${Layer.consensus}-label`}
        onClick={() => onSelectLayer(Layer.consensus)}
        d="M172.232 172.508C171.688 172.508 171.206 172.385 170.784 172.14C170.363 171.889 170.032 171.548 169.792 171.116C169.552 170.679 169.432 170.177 169.432 169.612C169.432 169.047 169.552 168.548 169.792 168.116C170.032 167.679 170.36 167.337 170.776 167.092C171.198 166.841 171.68 166.716 172.224 166.716C172.608 166.716 172.966 166.785 173.296 166.924C173.627 167.057 173.915 167.247 174.16 167.492C174.411 167.732 174.603 168.009 174.736 168.324L173.72 168.7C173.63 168.503 173.51 168.332 173.36 168.188C173.211 168.039 173.04 167.924 172.848 167.844C172.656 167.759 172.448 167.716 172.224 167.716C171.899 167.716 171.608 167.799 171.352 167.964C171.102 168.124 170.904 168.345 170.76 168.628C170.622 168.911 170.552 169.239 170.552 169.612C170.552 169.985 170.624 170.316 170.768 170.604C170.912 170.887 171.112 171.111 171.368 171.276C171.624 171.436 171.918 171.516 172.248 171.516C172.478 171.516 172.686 171.473 172.872 171.388C173.064 171.297 173.232 171.175 173.376 171.02C173.526 170.865 173.646 170.687 173.736 170.484L174.752 170.852C174.619 171.177 174.427 171.465 174.176 171.716C173.931 171.967 173.643 172.161 173.312 172.3C172.982 172.439 172.622 172.508 172.232 172.508ZM177.285 172.508C176.88 172.508 176.517 172.42 176.197 172.244C175.883 172.063 175.635 171.815 175.453 171.5C175.277 171.185 175.189 170.823 175.189 170.412C175.189 170.001 175.277 169.639 175.453 169.324C175.629 169.009 175.875 168.764 176.189 168.588C176.504 168.407 176.864 168.316 177.269 168.316C177.68 168.316 178.043 168.407 178.357 168.588C178.672 168.764 178.917 169.009 179.093 169.324C179.269 169.639 179.357 170.001 179.357 170.412C179.357 170.823 179.269 171.185 179.093 171.5C178.917 171.815 178.672 172.063 178.357 172.244C178.048 172.42 177.691 172.508 177.285 172.508ZM177.285 171.532C177.483 171.532 177.656 171.484 177.805 171.388C177.955 171.292 178.069 171.161 178.149 170.996C178.235 170.825 178.277 170.631 178.277 170.412C178.277 170.193 178.235 170.001 178.149 169.836C178.064 169.665 177.944 169.532 177.789 169.436C177.64 169.34 177.467 169.292 177.269 169.292C177.077 169.292 176.904 169.34 176.749 169.436C176.6 169.532 176.483 169.665 176.397 169.836C176.312 170.001 176.269 170.193 176.269 170.412C176.269 170.631 176.312 170.825 176.397 170.996C176.483 171.161 176.603 171.292 176.757 171.388C176.912 171.484 177.088 171.532 177.285 171.532ZM180.054 172.412V168.412H181.046L181.11 169.148V172.412H180.054ZM182.702 172.412V170.364L183.758 170.124V172.412H182.702ZM182.702 170.364C182.702 170.092 182.67 169.881 182.606 169.732C182.547 169.583 182.462 169.476 182.35 169.412C182.238 169.343 182.107 169.308 181.958 169.308C181.686 169.303 181.475 169.385 181.326 169.556C181.182 169.727 181.11 169.98 181.11 170.316H180.758C180.758 169.889 180.819 169.529 180.942 169.236C181.07 168.937 181.246 168.711 181.47 168.556C181.694 168.396 181.958 168.316 182.262 168.316C182.747 168.316 183.118 168.465 183.374 168.764C183.635 169.057 183.763 169.511 183.758 170.124L182.702 170.364ZM186.097 172.508C185.841 172.508 185.601 172.471 185.377 172.396C185.153 172.316 184.955 172.204 184.785 172.06C184.614 171.911 184.481 171.729 184.385 171.516L185.289 171.1C185.369 171.228 185.478 171.34 185.617 171.436C185.755 171.527 185.915 171.572 186.097 171.572C186.273 171.572 186.409 171.548 186.505 171.5C186.601 171.447 186.649 171.372 186.649 171.276C186.649 171.18 186.609 171.111 186.529 171.068C186.454 171.02 186.35 170.98 186.217 170.948L185.849 170.852C185.465 170.751 185.161 170.591 184.937 170.372C184.718 170.148 184.609 169.892 184.609 169.604C184.609 169.193 184.739 168.876 185.001 168.652C185.267 168.428 185.643 168.316 186.129 168.316C186.379 168.316 186.611 168.353 186.825 168.428C187.043 168.503 187.23 168.607 187.385 168.74C187.539 168.873 187.646 169.028 187.705 169.204L186.833 169.604C186.795 169.497 186.705 169.412 186.561 169.348C186.417 169.279 186.273 169.244 186.129 169.244C185.985 169.244 185.873 169.276 185.793 169.34C185.718 169.399 185.681 169.481 185.681 169.588C185.681 169.657 185.718 169.716 185.793 169.764C185.867 169.807 185.974 169.844 186.113 169.876L186.633 170.004C186.894 170.068 187.102 170.172 187.257 170.316C187.417 170.455 187.531 170.612 187.601 170.788C187.675 170.959 187.713 171.127 187.713 171.292C187.713 171.532 187.641 171.745 187.497 171.932C187.358 172.113 187.166 172.255 186.921 172.356C186.681 172.457 186.406 172.508 186.097 172.508ZM190.135 172.508C189.74 172.508 189.391 172.42 189.087 172.244C188.783 172.063 188.543 171.815 188.367 171.5C188.196 171.185 188.111 170.823 188.111 170.412C188.111 170.001 188.199 169.639 188.375 169.324C188.556 169.009 188.802 168.764 189.111 168.588C189.426 168.407 189.786 168.316 190.191 168.316C190.538 168.316 190.858 168.409 191.151 168.596C191.45 168.777 191.69 169.047 191.871 169.404C192.052 169.756 192.143 170.185 192.143 170.692H191.071C191.076 170.34 191.039 170.06 190.959 169.852C190.884 169.639 190.778 169.487 190.639 169.396C190.5 169.3 190.338 169.252 190.151 169.252C189.943 169.252 189.767 169.295 189.623 169.38C189.479 169.465 189.37 169.596 189.295 169.772C189.226 169.943 189.191 170.167 189.191 170.444C189.191 170.684 189.239 170.884 189.335 171.044C189.431 171.204 189.559 171.327 189.719 171.412C189.879 171.492 190.055 171.532 190.247 171.532C190.455 171.532 190.623 171.487 190.751 171.396C190.884 171.305 190.986 171.188 191.055 171.044L192.047 171.428C191.935 171.652 191.788 171.847 191.607 172.012C191.431 172.172 191.22 172.295 190.975 172.38C190.73 172.465 190.45 172.508 190.135 172.508ZM188.799 170.692L188.807 169.868H191.623V170.692H188.799ZM192.757 172.412V168.412H193.749L193.813 169.148V172.412H192.757ZM195.405 172.412V170.364L196.461 170.124V172.412H195.405ZM195.405 170.364C195.405 170.092 195.373 169.881 195.309 169.732C195.25 169.583 195.165 169.476 195.053 169.412C194.941 169.343 194.81 169.308 194.661 169.308C194.389 169.303 194.178 169.385 194.029 169.556C193.885 169.727 193.813 169.98 193.813 170.316H193.461C193.461 169.889 193.522 169.529 193.645 169.236C193.773 168.937 193.949 168.711 194.173 168.556C194.397 168.396 194.661 168.316 194.965 168.316C195.45 168.316 195.821 168.465 196.077 168.764C196.338 169.057 196.466 169.511 196.461 170.124L195.405 170.364ZM198.8 172.508C198.544 172.508 198.304 172.471 198.08 172.396C197.856 172.316 197.658 172.204 197.488 172.06C197.317 171.911 197.184 171.729 197.088 171.516L197.992 171.1C198.072 171.228 198.181 171.34 198.32 171.436C198.458 171.527 198.618 171.572 198.8 171.572C198.976 171.572 199.112 171.548 199.208 171.5C199.304 171.447 199.352 171.372 199.352 171.276C199.352 171.18 199.312 171.111 199.232 171.068C199.157 171.02 199.053 170.98 198.92 170.948L198.552 170.852C198.168 170.751 197.864 170.591 197.64 170.372C197.421 170.148 197.312 169.892 197.312 169.604C197.312 169.193 197.442 168.876 197.704 168.652C197.97 168.428 198.346 168.316 198.832 168.316C199.082 168.316 199.314 168.353 199.528 168.428C199.746 168.503 199.933 168.607 200.088 168.74C200.242 168.873 200.349 169.028 200.408 169.204L199.536 169.604C199.498 169.497 199.408 169.412 199.264 169.348C199.12 169.279 198.976 169.244 198.832 169.244C198.688 169.244 198.576 169.276 198.496 169.34C198.421 169.399 198.384 169.481 198.384 169.588C198.384 169.657 198.421 169.716 198.496 169.764C198.57 169.807 198.677 169.844 198.816 169.876L199.336 170.004C199.597 170.068 199.805 170.172 199.96 170.316C200.12 170.455 200.234 170.612 200.304 170.788C200.378 170.959 200.416 171.127 200.416 171.292C200.416 171.532 200.344 171.745 200.2 171.932C200.061 172.113 199.869 172.255 199.624 172.356C199.384 172.457 199.109 172.508 198.8 172.508ZM203.67 172.412L203.606 171.676V168.412H204.662V172.412H203.67ZM200.958 170.7V168.412H202.014V170.46L200.958 170.7ZM202.014 170.46C202.014 170.727 202.044 170.937 202.102 171.092C202.166 171.241 202.254 171.351 202.366 171.42C202.484 171.484 202.614 171.516 202.758 171.516C203.036 171.521 203.246 171.439 203.39 171.268C203.534 171.092 203.606 170.839 203.606 170.508H203.966C203.966 170.935 203.902 171.297 203.774 171.596C203.652 171.889 203.476 172.116 203.246 172.276C203.022 172.431 202.758 172.508 202.454 172.508C201.974 172.508 201.604 172.361 201.342 172.068C201.086 171.769 200.958 171.313 200.958 170.7L202.014 170.46ZM207.073 172.508C206.817 172.508 206.577 172.471 206.353 172.396C206.129 172.316 205.932 172.204 205.761 172.06C205.59 171.911 205.457 171.729 205.361 171.516L206.265 171.1C206.345 171.228 206.454 171.34 206.593 171.436C206.732 171.527 206.892 171.572 207.073 171.572C207.249 171.572 207.385 171.548 207.481 171.5C207.577 171.447 207.625 171.372 207.625 171.276C207.625 171.18 207.585 171.111 207.505 171.068C207.43 171.02 207.326 170.98 207.193 170.948L206.825 170.852C206.441 170.751 206.137 170.591 205.913 170.372C205.694 170.148 205.585 169.892 205.585 169.604C205.585 169.193 205.716 168.876 205.977 168.652C206.244 168.428 206.62 168.316 207.105 168.316C207.356 168.316 207.588 168.353 207.801 168.428C208.02 168.503 208.206 168.607 208.361 168.74C208.516 168.873 208.622 169.028 208.681 169.204L207.809 169.604C207.772 169.497 207.681 169.412 207.537 169.348C207.393 169.279 207.249 169.244 207.105 169.244C206.961 169.244 206.849 169.276 206.769 169.34C206.694 169.399 206.657 169.481 206.657 169.588C206.657 169.657 206.694 169.716 206.769 169.764C206.844 169.807 206.95 169.844 207.089 169.876L207.609 170.004C207.87 170.068 208.078 170.172 208.233 170.316C208.393 170.455 208.508 170.612 208.577 170.788C208.652 170.959 208.689 171.127 208.689 171.292C208.689 171.532 208.617 171.745 208.473 171.932C208.334 172.113 208.142 172.255 207.897 172.356C207.657 172.457 207.382 172.508 207.073 172.508Z"
        fill="white"
      />
      <g style={{ pointerEvents: selectedLayer === Layer.emerald ? 'auto' : 'none' }}>
        <Link to={RouteUtils.getLatestTransactionsRoute(Layer.emerald)}>
          <circle
            cx="234.552"
            cy="36.6031"
            r="11.8332"
            transform="rotate(-90 234.552 36.6031)"
            fill="#01F1E3"
          />
          <circle
            cx="234.482"
            cy="36.6743"
            r="8.02878"
            transform="rotate(-90 234.482 36.6743)"
            fill="#191932"
          />
          <text x="231.5" y="38" fill="#fff" fontSize="4px">
            {t('home.txn')}
          </text>
        </Link>
        <Link to={RouteUtils.getLatestBlocksRoute(Layer.emerald)}>
          <circle
            cx="155.943"
            cy="37.8709"
            r="11.8332"
            transform="rotate(-90 155.943 37.8709)"
            fill="#01F1E3"
          />
          <circle
            cx="155.873"
            cy="37.9424"
            r="8.02878"
            transform="rotate(-90 155.873 37.9424)"
            fill="#191932"
          />
          <text x="150.5" y="39" fill="#fff" fontSize="4px">
            {t('home.blocks')}
          </text>
        </Link>
      </g>

      <g style={{ pointerEvents: selectedLayer === Layer.sapphire ? 'auto' : 'none' }}>
        <Link to={RouteUtils.getLatestTransactionsRoute(Layer.sapphire)}>
          <circle
            cx="155.833"
            cy="295.833"
            r="11.8332"
            transform="rotate(-90 155.833 295.833)"
            fill="#01F1E3"
          />
          <circle
            cx="155.763"
            cy="295.904"
            r="8.02878"
            transform="rotate(-90 155.763 295.904)"
            fill="#191932"
          />
          <text x="152.5" y="297" fill="#fff" fontSize="4px">
            {t('home.txn')}
          </text>
        </Link>
        <Link to={RouteUtils.getLatestBlocksRoute(Layer.sapphire)}>
          <circle
            cx="79.8332"
            cy="287.833"
            r="11.8332"
            transform="rotate(-90 79.8332 287.833)"
            fill="#01F1E3"
          />
          <circle
            cx="79.7632"
            cy="287.905"
            r="8.02878"
            transform="rotate(-90 79.7632 287.905)"
            fill="#191932"
          />
          <text x="74.25" y="289" fill="#fff" fontSize="4px">
            {t('home.blocks')}
          </text>
        </Link>
      </g>

      <path
        d="M257.945 180.074C259.098 170.954 258.445 161.696 256.021 152.828C253.597 143.961 249.45 135.658 243.817 128.393C238.184 121.128 231.175 115.044 223.191 110.488C215.206 105.932 206.403 102.993 197.283 101.839C188.163 100.685 178.904 101.339 170.037 103.763C161.17 106.187 152.866 110.334 145.602 115.967C138.337 121.6 132.253 128.609 127.697 136.593C123.14 144.577 120.201 153.381 119.048 162.501C117.894 171.621 118.548 180.879 120.972 189.747C123.396 198.614 127.543 206.917 133.176 214.182C138.809 221.447 145.817 227.531 153.802 232.087C161.786 236.643 170.589 239.582 179.71 240.736C188.83 241.89 198.088 241.236 206.955 238.812C215.823 236.388 224.126 232.241 231.391 226.608C238.655 220.975 244.74 213.966 249.296 205.982C253.852 197.998 256.791 189.194 257.945 180.074L257.945 180.074Z"
        stroke="#00FEFF"
        strokeWidth="6.16169"
        strokeDasharray="0.27 2.71"
      />
      <GraphTooltip
        open={showZoomedInLayerTooltip === Layer.emerald}
        onOpen={() => handleOpenZoomedInLayerTooltip(Layer.emerald)}
        onClose={handleCloseZoomedInLayerTooltip}
        onClick={() => {
          onSelectLayer(Layer.emerald)
        }}
        offsetWidth={emeraldRect?.width}
        offsetHeight={emeraldRect?.height}
        layer={Layer.emerald}
      >
        <g
          ref={emeraldRef}
          id={`${Layer.emerald}-circle`}
          aria-disabled={disabledMap[Layer.emerald]}
          onClick={() => onSelectLayer(Layer.emerald)}
          filter="url(#filter0_dii_3537_177608)"
        >
          <circle cx="195.67" cy="63.7931" r="21.1306" fill="#030092" />
          <circle cx="195.67" cy="63.7931" r="21.1306" fill="url(#paint4_radial_3537_177608)" />
        </g>
      </GraphTooltip>
      <path
        id={`${Layer.emerald}-label`}
        aria-disabled={disabledMap[Layer.emerald]}
        onClick={() => onSelectLayer(Layer.emerald)}
        d="M180.119 65.7493V60.1493H180.799V65.7493H180.119ZM180.719 65.7493V65.1333H183.447V65.7493H180.719ZM180.719 63.1813V62.5733H183.199V63.1813H180.719ZM180.719 60.7653V60.1493H183.431V60.7653H180.719ZM184.669 65.7493V61.7493H185.269L185.309 62.4853V65.7493H184.669ZM187.477 65.7493V63.7013L188.117 63.4613V65.7493H187.477ZM190.293 65.7493V63.7013L190.933 63.4613V65.7493H190.293ZM187.477 63.7013C187.477 63.3066 187.432 63.0053 187.341 62.7973C187.251 62.5893 187.125 62.4479 186.965 62.3733C186.805 62.2933 186.624 62.2533 186.421 62.2533C186.069 62.2533 185.795 62.3786 185.597 62.6293C185.405 62.8746 185.309 63.2239 185.309 63.6773H184.997C184.997 63.2559 185.059 62.8933 185.181 62.5893C185.304 62.2853 185.48 62.0533 185.709 61.8933C185.944 61.7333 186.227 61.6533 186.557 61.6533C187.043 61.6533 187.424 61.8026 187.701 62.1013C187.984 62.3999 188.123 62.8533 188.117 63.4613L187.477 63.7013ZM190.293 63.7013C190.293 63.3066 190.245 63.0053 190.149 62.7973C190.059 62.5893 189.933 62.4479 189.773 62.3733C189.613 62.2933 189.432 62.2533 189.229 62.2533C188.877 62.2533 188.603 62.3786 188.405 62.6293C188.213 62.8746 188.117 63.2239 188.117 63.6773H187.805C187.805 63.2559 187.867 62.8933 187.989 62.5893C188.112 62.2853 188.291 62.0533 188.525 61.8933C188.76 61.7333 189.043 61.6533 189.373 61.6533C189.853 61.6533 190.232 61.8026 190.509 62.1013C190.792 62.3999 190.933 62.8533 190.933 63.4613L190.293 63.7013ZM193.695 65.8453C193.305 65.8453 192.961 65.7573 192.663 65.5813C192.364 65.3999 192.129 65.1519 191.959 64.8373C191.793 64.5226 191.711 64.1599 191.711 63.7493C191.711 63.3386 191.793 62.9759 191.959 62.6613C192.129 62.3466 192.361 62.1013 192.655 61.9253C192.953 61.7439 193.295 61.6533 193.679 61.6533C194.047 61.6533 194.367 61.7466 194.639 61.9333C194.916 62.1146 195.129 62.3706 195.279 62.7013C195.428 63.0319 195.503 63.4213 195.503 63.8693H194.863C194.863 63.5279 194.817 63.2373 194.727 62.9973C194.636 62.7519 194.5 62.5626 194.319 62.4293C194.143 62.2959 193.927 62.2293 193.671 62.2293C193.409 62.2293 193.177 62.2933 192.975 62.4213C192.777 62.5439 192.625 62.7199 192.519 62.9493C192.412 63.1786 192.359 63.4479 192.359 63.7573C192.359 64.0613 192.417 64.3253 192.535 64.5493C192.652 64.7679 192.815 64.9413 193.023 65.0693C193.231 65.1919 193.465 65.2533 193.727 65.2533C194.004 65.2533 194.239 65.1866 194.431 65.0533C194.623 64.9199 194.775 64.7466 194.887 64.5333L195.447 64.8133C195.345 65.0213 195.209 65.2026 195.039 65.3573C194.873 65.5119 194.676 65.6319 194.447 65.7173C194.223 65.8026 193.972 65.8453 193.695 65.8453ZM192.135 63.8693L192.143 63.3173H195.111V63.8693H192.135ZM196.662 63.5813C196.662 63.1493 196.745 62.7999 196.91 62.5333C197.075 62.2613 197.286 62.0613 197.542 61.9333C197.803 61.7999 198.078 61.7333 198.366 61.7333V62.3333C198.131 62.3333 197.905 62.3733 197.686 62.4533C197.473 62.5279 197.297 62.6533 197.158 62.8293C197.019 63.0053 196.95 63.2453 196.95 63.5493L196.662 63.5813ZM196.31 65.7493V61.7493H196.95V65.7493H196.31ZM201.477 65.7493L201.437 65.0933V63.6373C201.437 63.3226 201.4 63.0639 201.325 62.8613C201.256 62.6533 201.146 62.4986 200.997 62.3973C200.853 62.2906 200.661 62.2373 200.421 62.2373C200.192 62.2373 199.992 62.2826 199.821 62.3733C199.656 62.4639 199.52 62.6053 199.413 62.7973L198.837 62.5653C198.944 62.3786 199.072 62.2159 199.221 62.0773C199.37 61.9386 199.544 61.8346 199.741 61.7653C199.944 61.6906 200.173 61.6533 200.429 61.6533C200.797 61.6533 201.104 61.7253 201.349 61.8693C201.594 62.0133 201.778 62.2293 201.901 62.5173C202.024 62.7999 202.082 63.1546 202.077 63.5813L202.069 65.7493H201.477ZM200.277 65.8453C199.808 65.8453 199.44 65.7386 199.173 65.5253C198.912 65.3066 198.781 65.0053 198.781 64.6213C198.781 64.2159 198.914 63.9066 199.181 63.6933C199.453 63.4746 199.832 63.3653 200.317 63.3653H201.461V63.9013H200.461C200.088 63.9013 199.821 63.9626 199.661 64.0853C199.506 64.2079 199.429 64.3839 199.429 64.6133C199.429 64.8213 199.506 64.9866 199.661 65.1093C199.816 65.2266 200.032 65.2853 200.309 65.2853C200.538 65.2853 200.738 65.2319 200.909 65.1253C201.08 65.0186 201.21 64.8746 201.301 64.6933C201.392 64.5066 201.437 64.2959 201.437 64.0613H201.829C201.829 64.6053 201.696 65.0399 201.429 65.3653C201.162 65.6853 200.778 65.8453 200.277 65.8453ZM203.091 65.7493V60.1493H203.731V65.7493H203.091ZM207.809 65.7493L207.777 65.0053V60.1493H208.417V65.7493H207.809ZM206.425 65.8453C206.068 65.8453 205.753 65.7573 205.481 65.5813C205.209 65.4053 204.996 65.1599 204.841 64.8453C204.692 64.5253 204.617 64.1599 204.617 63.7493C204.617 63.3333 204.692 62.9679 204.841 62.6533C204.996 62.3386 205.209 62.0933 205.481 61.9173C205.753 61.7413 206.068 61.6533 206.425 61.6533C206.761 61.6533 207.054 61.7413 207.305 61.9173C207.561 62.0933 207.758 62.3386 207.897 62.6533C208.036 62.9679 208.105 63.3333 208.105 63.7493C208.105 64.1599 208.036 64.5253 207.897 64.8453C207.758 65.1599 207.561 65.4053 207.305 65.5813C207.054 65.7573 206.761 65.8453 206.425 65.8453ZM206.569 65.2613C206.804 65.2613 207.012 65.1973 207.193 65.0693C207.374 64.9413 207.516 64.7626 207.617 64.5333C207.724 64.3039 207.777 64.0426 207.777 63.7493C207.777 63.4559 207.724 63.1946 207.617 62.9653C207.516 62.7359 207.374 62.5573 207.193 62.4293C207.012 62.3013 206.801 62.2373 206.561 62.2373C206.31 62.2373 206.086 62.3013 205.889 62.4293C205.697 62.5573 205.545 62.7359 205.433 62.9653C205.321 63.1946 205.265 63.4559 205.265 63.7493C205.265 64.0426 205.321 64.3039 205.433 64.5333C205.545 64.7626 205.7 64.9413 205.897 65.0693C206.094 65.1973 206.318 65.2613 206.569 65.2613Z"
        fill="white"
      />
      <GraphTooltip offsetWidth={cipherRect?.width} offsetHeight={cipherRect?.height} layer={Layer.cipher}>
        <g
          ref={cipherRef}
          aria-disabled={disabledMap[Layer.cipher]}
          onClick={() => onSelectLayer(Layer.cipher)}
          id={`${Layer.cipher}-circle`}
          filter="url(#filter1_dii_3537_177608)"
        >
          <circle cx="305.127" cy="127.608" r="21.1306" fill="#030092" />
          <circle cx="305.127" cy="127.608" r="21.1306" fill="url(#paint5_radial_3537_177608)" />
        </g>
      </GraphTooltip>
      <path
        id={`${Layer.cipher}-label`}
        aria-disabled={disabledMap[Layer.sapphire]}
        onClick={() => onSelectLayer(Layer.cipher)}
        d="M296.842 129.66C296.309 129.66 295.837 129.537 295.426 129.292C295.016 129.041 294.693 128.7 294.458 128.268C294.224 127.831 294.106 127.329 294.106 126.764C294.106 126.199 294.221 125.7 294.45 125.268C294.685 124.831 295.005 124.489 295.41 124.244C295.821 123.993 296.29 123.868 296.818 123.868C297.154 123.868 297.477 123.929 297.786 124.052C298.096 124.169 298.373 124.337 298.618 124.556C298.864 124.769 299.053 125.017 299.186 125.3L298.602 125.596C298.496 125.372 298.349 125.177 298.162 125.012C297.976 124.847 297.768 124.719 297.538 124.628C297.309 124.532 297.069 124.484 296.818 124.484C296.429 124.484 296.08 124.583 295.77 124.78C295.461 124.972 295.218 125.239 295.042 125.58C294.872 125.921 294.786 126.316 294.786 126.764C294.786 127.212 294.874 127.609 295.05 127.956C295.226 128.303 295.469 128.575 295.778 128.772C296.093 128.964 296.448 129.06 296.842 129.06C297.098 129.06 297.341 129.009 297.57 128.908C297.8 128.801 298.005 128.657 298.186 128.476C298.373 128.295 298.52 128.087 298.626 127.852L299.218 128.148C299.08 128.441 298.888 128.703 298.642 128.932C298.402 129.161 298.125 129.34 297.81 129.468C297.501 129.596 297.178 129.66 296.842 129.66ZM300.104 129.564L300.12 125.564H300.76L300.744 129.564H300.104ZM300.432 124.636C300.325 124.636 300.232 124.596 300.152 124.516C300.072 124.436 300.032 124.343 300.032 124.236C300.032 124.124 300.072 124.031 300.152 123.956C300.232 123.876 300.325 123.836 300.432 123.836C300.544 123.836 300.637 123.876 300.712 123.956C300.792 124.031 300.832 124.124 300.832 124.236C300.832 124.343 300.792 124.436 300.712 124.516C300.637 124.596 300.544 124.636 300.432 124.636ZM301.963 131.164V125.564H302.563L302.603 126.3V131.164H301.963ZM303.955 129.66C303.619 129.66 303.323 129.572 303.067 129.396C302.817 129.22 302.622 128.975 302.483 128.66C302.345 128.34 302.275 127.975 302.275 127.564C302.275 127.148 302.345 126.783 302.483 126.468C302.622 126.153 302.817 125.908 303.067 125.732C303.323 125.556 303.619 125.468 303.955 125.468C304.313 125.468 304.627 125.556 304.899 125.732C305.171 125.908 305.382 126.153 305.531 126.468C305.686 126.783 305.763 127.148 305.763 127.564C305.763 127.975 305.686 128.34 305.531 128.66C305.382 128.975 305.171 129.22 304.899 129.396C304.627 129.572 304.313 129.66 303.955 129.66ZM303.811 129.076C304.062 129.076 304.283 129.012 304.475 128.884C304.673 128.756 304.827 128.577 304.939 128.348C305.057 128.119 305.115 127.857 305.115 127.564C305.115 127.271 305.059 127.009 304.947 126.78C304.841 126.551 304.689 126.372 304.491 126.244C304.294 126.116 304.07 126.052 303.819 126.052C303.585 126.052 303.374 126.116 303.187 126.244C303.006 126.372 302.862 126.551 302.755 126.78C302.654 127.009 302.603 127.271 302.603 127.564C302.603 127.857 302.654 128.119 302.755 128.348C302.862 128.577 303.006 128.756 303.187 128.884C303.369 129.012 303.577 129.076 303.811 129.076ZM306.65 129.564V123.86H307.29V129.564H306.65ZM309.466 129.564V127.516L310.106 127.276V129.564H309.466ZM309.466 127.516C309.466 127.121 309.418 126.82 309.322 126.612C309.231 126.404 309.106 126.263 308.946 126.188C308.786 126.108 308.605 126.068 308.402 126.068C308.05 126.068 307.775 126.193 307.578 126.444C307.386 126.689 307.29 127.039 307.29 127.492H306.978C306.978 127.071 307.039 126.708 307.162 126.404C307.285 126.1 307.463 125.868 307.698 125.708C307.933 125.548 308.215 125.468 308.546 125.468C309.026 125.468 309.407 125.617 309.69 125.916C309.973 126.215 310.111 126.668 310.106 127.276L309.466 127.516ZM312.863 129.66C312.473 129.66 312.129 129.572 311.831 129.396C311.532 129.215 311.297 128.967 311.127 128.652C310.961 128.337 310.879 127.975 310.879 127.564C310.879 127.153 310.961 126.791 311.127 126.476C311.297 126.161 311.529 125.916 311.823 125.74C312.121 125.559 312.463 125.468 312.847 125.468C313.215 125.468 313.535 125.561 313.807 125.748C314.084 125.929 314.297 126.185 314.447 126.516C314.596 126.847 314.671 127.236 314.671 127.684H314.031C314.031 127.343 313.985 127.052 313.895 126.812C313.804 126.567 313.668 126.377 313.487 126.244C313.311 126.111 313.095 126.044 312.839 126.044C312.577 126.044 312.345 126.108 312.143 126.236C311.945 126.359 311.793 126.535 311.687 126.764C311.58 126.993 311.527 127.263 311.527 127.572C311.527 127.876 311.585 128.14 311.703 128.364C311.82 128.583 311.983 128.756 312.191 128.884C312.399 129.007 312.633 129.068 312.895 129.068C313.172 129.068 313.407 129.001 313.599 128.868C313.791 128.735 313.943 128.561 314.055 128.348L314.615 128.628C314.513 128.836 314.377 129.017 314.207 129.172C314.041 129.327 313.844 129.447 313.615 129.532C313.391 129.617 313.14 129.66 312.863 129.66ZM311.303 127.684L311.311 127.132H314.279V127.684H311.303ZM315.83 127.396C315.83 126.964 315.913 126.615 316.078 126.348C316.243 126.076 316.454 125.876 316.71 125.748C316.971 125.615 317.246 125.548 317.534 125.548V126.148C317.299 126.148 317.073 126.188 316.854 126.268C316.641 126.343 316.465 126.468 316.326 126.644C316.187 126.82 316.118 127.06 316.118 127.364L315.83 127.396ZM315.478 129.564V125.564H316.118V129.564H315.478Z"
        fill="white"
      />
      <GraphTooltip
        open={showZoomedInLayerTooltip === Layer.sapphire}
        onOpen={() => handleOpenZoomedInLayerTooltip(Layer.sapphire)}
        onClose={handleCloseZoomedInLayerTooltip}
        onClick={() => {
          onSelectLayer(Layer.sapphire)
        }}
        offsetWidth={sapphireRect?.width}
        offsetHeight={sapphireRect?.height}
        layer={Layer.sapphire}
      >
        <g
          ref={sapphireRef}
          id={`${Layer.sapphire}-circle`}
          aria-disabled={disabledMap[Layer.sapphire]}
          onClick={() => onSelectLayer(Layer.sapphire)}
          filter="url(#filter2_dii_3537_177608)"
        >
          <circle cx="123.826" cy="257.35" r="21.1306" fill="#030092" />
          <circle cx="123.826" cy="257.35" r="21.1306" fill="url(#paint6_radial_3537_177608)" />
        </g>
      </GraphTooltip>
      <path
        id={`${Layer.sapphire}-label`}
        aria-disabled={disabledMap[Layer.sapphire]}
        onClick={() => onSelectLayer(Layer.sapphire)}
        d="M110.575 259.402C110.266 259.402 109.991 259.37 109.751 259.306C109.511 259.237 109.303 259.143 109.127 259.026C108.951 258.903 108.807 258.77 108.695 258.626C108.583 258.477 108.5 258.319 108.447 258.154L109.095 257.93C109.175 258.165 109.335 258.37 109.575 258.546C109.82 258.717 110.119 258.802 110.471 258.802C110.903 258.802 111.242 258.717 111.487 258.546C111.732 258.375 111.855 258.149 111.855 257.866C111.855 257.605 111.746 257.394 111.527 257.234C111.308 257.069 111.02 256.941 110.663 256.85L110.047 256.69C109.796 256.626 109.564 256.533 109.351 256.41C109.143 256.282 108.975 256.122 108.847 255.93C108.724 255.733 108.663 255.498 108.663 255.226C108.663 254.719 108.826 254.325 109.151 254.042C109.482 253.754 109.956 253.61 110.575 253.61C110.943 253.61 111.26 253.671 111.527 253.794C111.794 253.911 112.01 254.063 112.175 254.25C112.346 254.431 112.466 254.629 112.535 254.842L111.895 255.066C111.794 254.799 111.623 254.591 111.383 254.442C111.143 254.287 110.852 254.21 110.511 254.21C110.154 254.21 109.868 254.298 109.655 254.474C109.447 254.645 109.343 254.879 109.343 255.178C109.343 255.434 109.426 255.631 109.591 255.77C109.756 255.903 109.972 256.002 110.239 256.066L110.855 256.218C111.399 256.346 111.815 256.559 112.103 256.858C112.396 257.151 112.543 257.469 112.543 257.81C112.543 258.103 112.468 258.373 112.319 258.618C112.17 258.858 111.948 259.05 111.655 259.194C111.367 259.333 111.007 259.402 110.575 259.402ZM115.977 259.306L115.937 258.65V257.194C115.937 256.879 115.9 256.621 115.825 256.418C115.756 256.21 115.646 256.055 115.497 255.954C115.353 255.847 115.161 255.794 114.921 255.794C114.692 255.794 114.492 255.839 114.321 255.93C114.156 256.021 114.02 256.162 113.913 256.354L113.337 256.122C113.444 255.935 113.572 255.773 113.721 255.634C113.87 255.495 114.044 255.391 114.241 255.322C114.444 255.247 114.673 255.21 114.929 255.21C115.297 255.21 115.604 255.282 115.849 255.426C116.094 255.57 116.278 255.786 116.401 256.074C116.524 256.357 116.582 256.711 116.577 257.138L116.569 259.306H115.977ZM114.777 259.402C114.308 259.402 113.94 259.295 113.673 259.082C113.412 258.863 113.281 258.562 113.281 258.178C113.281 257.773 113.414 257.463 113.681 257.25C113.953 257.031 114.332 256.922 114.817 256.922H115.961V257.458H114.961C114.588 257.458 114.321 257.519 114.161 257.642C114.006 257.765 113.929 257.941 113.929 258.17C113.929 258.378 114.006 258.543 114.161 258.666C114.316 258.783 114.532 258.842 114.809 258.842C115.038 258.842 115.238 258.789 115.409 258.682C115.58 258.575 115.71 258.431 115.801 258.25C115.892 258.063 115.937 257.853 115.937 257.618H116.329C116.329 258.162 116.196 258.597 115.929 258.922C115.662 259.242 115.278 259.402 114.777 259.402ZM117.631 260.906V255.306H118.231L118.271 256.042V260.906H117.631ZM119.623 259.402C119.287 259.402 118.991 259.314 118.735 259.138C118.485 258.962 118.29 258.717 118.151 258.402C118.013 258.082 117.943 257.717 117.943 257.306C117.943 256.89 118.013 256.525 118.151 256.21C118.29 255.895 118.485 255.65 118.735 255.474C118.991 255.298 119.287 255.21 119.623 255.21C119.981 255.21 120.295 255.298 120.567 255.474C120.839 255.65 121.05 255.895 121.199 256.21C121.354 256.525 121.431 256.89 121.431 257.306C121.431 257.717 121.354 258.082 121.199 258.402C121.05 258.717 120.839 258.962 120.567 259.138C120.295 259.314 119.981 259.402 119.623 259.402ZM119.479 258.818C119.73 258.818 119.951 258.754 120.143 258.626C120.341 258.498 120.495 258.319 120.607 258.09C120.725 257.861 120.783 257.599 120.783 257.306C120.783 257.013 120.727 256.751 120.615 256.522C120.509 256.293 120.357 256.114 120.159 255.986C119.962 255.858 119.738 255.794 119.487 255.794C119.253 255.794 119.042 255.858 118.855 255.986C118.674 256.114 118.53 256.293 118.423 256.522C118.322 256.751 118.271 257.013 118.271 257.306C118.271 257.599 118.322 257.861 118.423 258.09C118.53 258.319 118.674 258.498 118.855 258.626C119.037 258.754 119.245 258.818 119.479 258.818ZM122.358 260.906V255.306H122.958L122.998 256.042V260.906H122.358ZM124.35 259.402C124.014 259.402 123.718 259.314 123.462 259.138C123.211 258.962 123.016 258.717 122.878 258.402C122.739 258.082 122.67 257.717 122.67 257.306C122.67 256.89 122.739 256.525 122.878 256.21C123.016 255.895 123.211 255.65 123.462 255.474C123.718 255.298 124.014 255.21 124.35 255.21C124.707 255.21 125.022 255.298 125.294 255.474C125.566 255.65 125.776 255.895 125.926 256.21C126.08 256.525 126.158 256.89 126.158 257.306C126.158 257.717 126.08 258.082 125.926 258.402C125.776 258.717 125.566 258.962 125.294 259.138C125.022 259.314 124.707 259.402 124.35 259.402ZM124.206 258.818C124.456 258.818 124.678 258.754 124.87 258.626C125.067 258.498 125.222 258.319 125.334 258.09C125.451 257.861 125.51 257.599 125.51 257.306C125.51 257.013 125.454 256.751 125.342 256.522C125.235 256.293 125.083 256.114 124.886 255.986C124.688 255.858 124.464 255.794 124.214 255.794C123.979 255.794 123.768 255.858 123.582 255.986C123.4 256.114 123.256 256.293 123.15 256.522C123.048 256.751 122.998 257.013 122.998 257.306C122.998 257.599 123.048 257.861 123.15 258.09C123.256 258.319 123.4 258.498 123.582 258.626C123.763 258.754 123.971 258.818 124.206 258.818ZM127.044 259.306V253.602H127.684V259.306H127.044ZM129.86 259.306V257.258L130.5 257.018V259.306H129.86ZM129.86 257.258C129.86 256.863 129.812 256.562 129.716 256.354C129.626 256.146 129.5 256.005 129.34 255.93C129.18 255.85 128.999 255.81 128.796 255.81C128.444 255.81 128.17 255.935 127.972 256.186C127.78 256.431 127.684 256.781 127.684 257.234H127.372C127.372 256.813 127.434 256.45 127.556 256.146C127.679 255.842 127.858 255.61 128.092 255.45C128.327 255.29 128.61 255.21 128.94 255.21C129.42 255.21 129.802 255.359 130.084 255.658C130.367 255.957 130.506 256.41 130.5 257.018L129.86 257.258ZM131.545 259.306L131.561 255.306H132.201L132.185 259.306H131.545ZM131.873 254.378C131.767 254.378 131.673 254.338 131.593 254.258C131.513 254.178 131.473 254.085 131.473 253.978C131.473 253.866 131.513 253.773 131.593 253.698C131.673 253.618 131.767 253.578 131.873 253.578C131.985 253.578 132.079 253.618 132.153 253.698C132.233 253.773 132.273 253.866 132.273 253.978C132.273 254.085 132.233 254.178 132.153 254.258C132.079 254.338 131.985 254.378 131.873 254.378ZM133.717 257.138C133.717 256.706 133.799 256.357 133.965 256.09C134.13 255.818 134.341 255.618 134.597 255.49C134.858 255.357 135.133 255.29 135.421 255.29V255.89C135.186 255.89 134.959 255.93 134.741 256.01C134.527 256.085 134.351 256.21 134.213 256.386C134.074 256.562 134.005 256.802 134.005 257.106L133.717 257.138ZM133.365 259.306V255.306H134.005V259.306H133.365ZM137.82 259.402C137.43 259.402 137.086 259.314 136.788 259.138C136.489 258.957 136.254 258.709 136.084 258.394C135.918 258.079 135.836 257.717 135.836 257.306C135.836 256.895 135.918 256.533 136.084 256.218C136.254 255.903 136.486 255.658 136.78 255.482C137.078 255.301 137.42 255.21 137.804 255.21C138.172 255.21 138.492 255.303 138.764 255.49C139.041 255.671 139.254 255.927 139.404 256.258C139.553 256.589 139.628 256.978 139.628 257.426H138.988C138.988 257.085 138.942 256.794 138.852 256.554C138.761 256.309 138.625 256.119 138.444 255.986C138.268 255.853 138.052 255.786 137.796 255.786C137.534 255.786 137.302 255.85 137.1 255.978C136.902 256.101 136.75 256.277 136.644 256.506C136.537 256.735 136.484 257.005 136.484 257.314C136.484 257.618 136.542 257.882 136.66 258.106C136.777 258.325 136.94 258.498 137.148 258.626C137.356 258.749 137.59 258.81 137.852 258.81C138.129 258.81 138.364 258.743 138.556 258.61C138.748 258.477 138.9 258.303 139.012 258.09L139.572 258.37C139.47 258.578 139.334 258.759 139.164 258.914C138.998 259.069 138.801 259.189 138.572 259.274C138.348 259.359 138.097 259.402 137.82 259.402ZM136.26 257.426L136.268 256.874H139.236V257.426H136.26Z"
        fill="white"
      />

      <defs>
        <filter
          id="filter0_dii_3537_177608"
          x="141.621"
          y="11.3904"
          width="108.098"
          height="108.097"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="1.6459" />
          <feGaussianBlur stdDeviation="16.459" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.135917 0 0 0 0 0 0 0 0 0 0.970833 0 0 0 1 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3537_177608" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_3537_177608" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dx="4.11475" dy="6.58359" />
          <feGaussianBlur stdDeviation="4.11475" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0.996078 0 0 0 0 1 0 0 0 0.95 0" />
          <feBlend mode="normal" in2="shape" result="effect2_innerShadow_3537_177608" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="0.822949" />
          <feGaussianBlur stdDeviation="2.05737" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.58 0" />
          <feBlend
            mode="normal"
            in2="effect2_innerShadow_3537_177608"
            result="effect3_innerShadow_3537_177608"
          />
        </filter>
        <filter
          id="filter1_dii_3537_177608"
          x="251.078"
          y="75.2049"
          width="108.098"
          height="108.097"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="1.6459" />
          <feGaussianBlur stdDeviation="16.459" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.135917 0 0 0 0 0 0 0 0 0 0.970833 0 0 0 1 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3537_177608" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_3537_177608" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dx="4.11475" dy="6.58359" />
          <feGaussianBlur stdDeviation="4.11475" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0.996078 0 0 0 0 1 0 0 0 0.95 0" />
          <feBlend mode="normal" in2="shape" result="effect2_innerShadow_3537_177608" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="0.822949" />
          <feGaussianBlur stdDeviation="2.05737" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.58 0" />
          <feBlend
            mode="normal"
            in2="effect2_innerShadow_3537_177608"
            result="effect3_innerShadow_3537_177608"
          />
        </filter>
        <filter
          id="filter2_dii_3537_177608"
          x="69.7773"
          y="204.947"
          width="108.098"
          height="108.097"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="1.6459" />
          <feGaussianBlur stdDeviation="16.459" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.135917 0 0 0 0 0 0 0 0 0 0.970833 0 0 0 1 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3537_177608" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_3537_177608" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dx="4.11475" dy="6.58359" />
          <feGaussianBlur stdDeviation="4.11475" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0.996078 0 0 0 0 1 0 0 0 0.95 0" />
          <feBlend mode="normal" in2="shape" result="effect2_innerShadow_3537_177608" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="0.822949" />
          <feGaussianBlur stdDeviation="2.05737" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.58 0" />
          <feBlend
            mode="normal"
            in2="effect2_innerShadow_3537_177608"
            result="effect3_innerShadow_3537_177608"
          />
        </filter>
        <linearGradient
          id="paint0_linear_3537_177608"
          x1="164.186"
          y1="44.3529"
          x2="180.879"
          y2="55.3408"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#02EAE4" />
          <stop offset="1" stopColor="#2C3BD5" />
        </linearGradient>
        <linearGradient
          id="paint1_linear_3537_177608"
          x1="225.043"
          y1="41.1834"
          x2="206.449"
          y2="56.1862"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#02EAE4" />
          <stop offset="1" stopColor="#2C3BD5" />
        </linearGradient>
        <linearGradient
          id="paint2_linear_3537_177608"
          x1="82.5657"
          y1="285.417"
          x2="98.9919"
          y2="274.034"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#02EAE4" />
          <stop offset="1" stopColor="#2C3BD5" />
        </linearGradient>
        <linearGradient
          id="paint3_linear_3537_177608"
          x1="152.058"
          y1="290.792"
          x2="135.141"
          y2="273.92"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#02EAE4" />
          <stop offset="1" stopColor="#2C3BD5" />
        </linearGradient>
        <radialGradient
          id="paint4_radial_3537_177608"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(204.291 55.848) rotate(110.973) scale(43.4495)"
        >
          <stop stopColor="#3244E8" />
          <stop offset="1" stopColor="#000210" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="paint5_radial_3537_177608"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(313.748 119.662) rotate(110.973) scale(43.4495)"
        >
          <stop stopColor="#3244E8" />
          <stop offset="1" stopColor="#000210" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="paint6_radial_3537_177608"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(132.447 249.405) rotate(110.973) scale(43.4495)"
        >
          <stop stopColor="#3244E8" />
          <stop offset="1" stopColor="#000210" stopOpacity="0" />
        </radialGradient>
      </defs>
    </GraphStyled>
  )
}

export const Graph = memo(forwardRef(GraphCmp))
